/**
 * "Soft" anonymous → paid handoff per v2 spec §12.
 *
 * Real spec wants the trial analysis itself transferred to the new user;
 * that requires anonymous trial uploads to be persisted in Firestore. We
 * don't do that today (trial uploads stay client-side). Instead we save
 * the user's *intent* — what they were about to execute when the paywall
 * fired — so after signup + re-upload we can pre-populate:
 *   - the upload-feedback modal (expectations + purpose)
 *   - the Studio command bar (the captured directive)
 *
 * That covers the conversion case where the user accepts the paywall but
 * would otherwise lose context across the signup flow.
 */

const STORAGE_KEY = 'studio_handoff_v1';
const TTL_MS = 24 * 60 * 60 * 1000;

function getStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

export function captureStudioHandoff({ intent = {}, userIntent = {}, source = 'paywall' } = {}) {
  const storage = getStorage();
  if (!storage) return;
  const payload = {
    intent: {
      kind: intent.kind || null,
      directive: typeof intent.directive === 'string' ? intent.directive.slice(0, 600) : null,
      directiveSource: intent.directiveSource || null,
      archetype: intent.archetype || null,
      suggestionId: intent.suggestionId || null,
    },
    userIntent: {
      expectations: typeof userIntent.expectations === 'string' ? userIntent.expectations.slice(0, 600) : null,
      purpose: typeof userIntent.purpose === 'string' ? userIntent.purpose : null,
    },
    source,
    capturedAt: Date.now(),
  };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // quota, private mode, etc. — swallow
  }
}

export function readStudioHandoff({ peek = false } = {}) {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.capturedAt !== 'number') {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.capturedAt > TTL_MS) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    if (!peek) {
      // Caller is going to consume the handoff — leave it for now and
      // let them clear() explicitly once the replay actually succeeds.
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearStudioHandoff() {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch (_) { /* ignore */ }
}

export function hasStudioHandoff() {
  return Boolean(readStudioHandoff({ peek: true }));
}
